import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ValidationUtilityService } from '../../shared/utils/validation.util/validation-utility.service';
import { MASK_CONSTANTS } from '../../../constants/mask.constants';
import { Config } from '../../../config/config';
import { Observable, of, Subscription } from 'rxjs';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { ServiceRouter } from '../../../services/core/http-service-router.service';
import { METHOD_TYPE } from '../../../constants/service.constants';
import { Environment } from '../../../config/environment';
import { AppUtil } from '../../../utils/app.util';
import { AnalyticsBean, HvaTrackingBean } from 'src/app/beans/analytics/analytics-module.bean';
import { MatDialog } from '@angular/material/dialog';
import { EheMeRedirectModalComponent } from '../../account/ehe-me-redirect-modal/ehe-me-redirect-modal.component';
import { EheVerificationModalComponent } from '../../shared/ehe-modal/verification-modal/ehe-verification-modal.component';
import { VerificationBean } from '../../../beans/account/account-module.bean';
import { EncIdModalComponent } from '../../account/mat-modal/enc-id-modal/enc-id-modal.component';
import { EheAnalyticsDirective } from '../../shared/ehe-analytics/ehe-analytics.directive';
import { EHEAndMeAuthService } from 'src/app/services/core/eheandme-auth.service';
import { DataCarrierService } from 'src/app/services/core/data-carrier.service';
import { MigrationNotificationModalComponent } from 'src/app/views/migration/migration-notification-modal/migration-notification-modal.component';
import { HVA_EVENT_TYPE, HVA_EVENT_STATUS } from '../../../constants/analytics.constants';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { INCENTIVE_CONSTANTS } from 'src/app/constants/scheduling.constants';
import { HttpParams } from '@angular/common/http';

/** Create account component */
@Component({
	selector: 'app-ehe-create-account',
	templateUrl: './create-account.component.html',
	styleUrls: ['./create-account.component.scss'],
	providers: [EheAnalyticsDirective],
})
export class CreateAccountComponent implements OnInit, OnDestroy {
	accountCreated: boolean = false;
	/** Create account form */
	createAccountForm: FormGroup;
	/** Default login URL */
	loginUrl = '/login';
	loginUrlBase = this.loginUrl;
	/** Default DOB Starting date */
	defaultDOBStartingDate: Date = new Date(2000, 0, 1);
	/** Masks for form control formatting during user text entry */
	dobMask = MASK_CONSTANTS.DATE;
	phoneMask = MASK_CONSTANTS.PHONE;
	/** Date Format */
	dateFormat: string = ValidationUtilityService.dateFormat;
	/** Employers list */
	employers: any = [];
	/** filtered employers list */
	filteredOptions: Observable<any>;
	/** */
	maxDate = new Date();
	/** denote whether to show loader in type-ahead */
	showTypeAheadLoader: boolean;
	/** Array of subscriptions used in the component */
	subscriptions: Subscription[] = [];
	/** Selected employer object */
	selectedEmployer: any;
	/** Selected employer name*/
	employerName: any;
	/** */
	dateOfBirth: any;
	/** Analytics data */
	analyticsData: AnalyticsBean;
	/** flag denotes invalid dob*/
	invalidDateOfBirth: boolean;
	/** flag denote whether the SSN field is required */
	ssnRequired: boolean;
	/** */
	showDuplicateEmailModal: boolean;
	/** List of clients required SSN */
	ssnAssociatedClients: any = [];
	/** Denote toggle SSN */
	showhideSSNVal = false;
	systemEmailFlow: boolean;
	/** SSN Verification attempt count */
	verificationAttemptCount: number;
	/** */
	verificationField: string;
	/** */
	employeeIdRequired: boolean;
	/** */
	oldEmployerName: string;
	/** */
	clearValue = false;
	/** Return Url */
	returnUrl = '';
	/** Error Details */
	errorDetails: any = null;
	/** Hides cell phone field */
	hideCellPhone: boolean = false;
	/** boolean to show/hide dob hint */
	showHint = true;
	hvaEvent: HvaTrackingBean;

	/**
	 * Create account component Constructor
	 * @param router - Angular Router
	 * @param serviceRouter - Service Router
	 * @param config - URL Config
	 * @param spinner - Spinner
	 * @param dialog - Angular material Dialog
	 * @param environment - Environment Service
	 * @param activatedRoute - Angular Activated route
	 * @param eheAnalyticsDirective - EHE Analytics Directive
	 */
	constructor(
		private router: Router,
		private serviceRouter: ServiceRouter,
		private config: Config,
		private spinner: SpinnerComponent,
		private dialog: MatDialog,
		private environment: Environment,
		private activatedRoute: ActivatedRoute,
		private eheAnalyticsDirective: EheAnalyticsDirective,
		private eheandmeAuthService: EHEAndMeAuthService,
		private dataCarrierService: DataCarrierService,
		private analyticsService: AnalyticsService
	) {
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'create-account';
		this.analyticsData.pageUrl = '/registration';
		this.invalidDateOfBirth = false;
		this.ssnRequired = false;

		// getting the list of SSN Associated clients from environment variable
		if (typeof environment.getEnvironment().SSN_ASSOCIATED_CLIENTS === 'string') {
			this.ssnAssociatedClients = environment.getEnvironment().SSN_ASSOCIATED_CLIENTS.split(',') || [];
		}
		// subscribing params
		this.activatedRoute.queryParams.subscribe(params => {
			if (params.enc_id) {
				this.systemEmailFlow = true;
				this.getSystemEmailFromEncID(params.enc_id);
			}
			if (params.returnUrl) {
				this.returnUrl = params.returnUrl;
			}
		});
		this.verificationAttemptCount = 0;
		this.systemEmailFlow = false;
		this.showDuplicateEmailModal = false;
		this.employeeIdRequired = false;
		this.hvaEvent = new HvaTrackingBean();
	}
	enableOptimize(event, id) {
		if (event === true) {
			if (id == 'optimize-registration') {
				this.hideCellPhone = true;
			}
		}
	}
	/** On init */
	ngOnInit() {
		if (this.router.routerState.snapshot.url.includes('embed')) {
			this.loginUrl = '/embed/login';
		}

		this.createAccountForm = new FormGroup({
			firstName: new FormControl('', Validators.compose([Validators.required, ValidationUtilityService.nameValidator])),
			lastName: new FormControl('', Validators.compose([Validators.required, ValidationUtilityService.nameValidator])),
			dateOfBirth: new FormControl(
				'',
				Validators.compose([
					Validators.required,
					ValidationUtilityService.dateValidator,
					ValidationUtilityService.dateValidatorNonFuture,
				])
			),
			employer: new FormControl(
				'',
				Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(200)])
			),
			email: new FormControl(
				{ value: '', disabled: this.systemEmailFlow },
				Validators.compose([
					Validators.required,
					Validators.minLength(2),
					Validators.maxLength(200),
					ValidationUtilityService.emailValidator,
				])
			),
			cellPhone: new FormControl('', ValidationUtilityService.optionalPhoneNumberValidator),
			privacy: new FormControl(false, Validators.requiredTrue),
			ssn: new FormControl(
				{ value: undefined, disabled: true },
				Validators.compose([Validators.required, ValidationUtilityService.ssnValidator])
			),
			employeeID: new FormControl({ value: undefined, disabled: true }, Validators.required),
		});

		this.dateOfBirth = this.createAccountForm.get('dateOfBirth');

		// Service call to get list of employers for type ahead
		// this.getEmployers();
		// Employer name typeahead field
		this.subscriptions.push(
			this.createAccountForm.get('employer').valueChanges.subscribe(value => {
				if (value) {
					if (
						(typeof value === 'object' &&
							this.ssnAssociatedClients.filter((clientName: string) => clientName === value.name.toUpperCase()).length >
								0) ||
						(typeof value !== 'object' &&
							this.ssnAssociatedClients.filter((clientName: string) => clientName === value.toUpperCase()).length > 0)
					) {
						this.ssnRequired = true;
						this.createAccountForm.get('ssn').setValue('', { emitEvent: false });
						this.createAccountForm.get('ssn').enable();
					} else {
						this.ssnRequired = false;
						this.createAccountForm.get('ssn').setValue(undefined, { emitEvent: false });
						this.createAccountForm.get('ssn').disable();
					}
					this.employerName = value;
					this.filter(value);
				}
			})
		);

		this.subscriptions.push(
			this.createAccountForm.get('dateOfBirth').valueChanges.subscribe(value => {
				// auto-zero pad opening month and day as a convenience, if it starts with 2-9
				ValidationUtilityService.zeroPadDateControl(<FormControl>this.createAccountForm.get('dateOfBirth'), value);
			})
		);
	}

	/** Toggle SSN */
	showHideSSN() {
		this.showhideSSNVal = !this.showhideSSNVal;
	}

	/**
	 * Get employers list
	 * @param searchField - searching text
	 */
	getEmployers(searchField = '') {
		searchField = searchField.trim();
		// sending service request only if the search text length greater than 2
		if (searchField.length > 2) {
			// showing loader on typeahead
			this.showTypeAheadLoader = true;
			this.subscriptions.push(
				this.serviceRouter
					.makeRequest(
						this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
						this.config.url.registrationApi.getEmployers + `?search=${encodeURIComponent(searchField)}`,
						METHOD_TYPE.GENERAL_GET,
						''
					)
					.subscribe(
						response => {
							// hiding loader on typeahead
							this.showTypeAheadLoader = false;
							if (response) {
								this.filteredOptions = of(response);
								this.employers = response;
							} else {
								this.filteredOptions = of([]);
								this.employers = [];
							}
						},
						error => {
							console.error(`[${CreateAccountComponent.name}][${this.getEmployers.name}]`, 'An error occurred while fetching employers data : ', error);
						},
						() => {
							this.showTypeAheadLoader = false;
						}
					)
			);
		}
	}

	/**
	 * Get employer name from object
	 * @param employer - Employer object
	 */
	getEmployerDisplayName(employer?: any): string | undefined {
		return employer ? employer.name : '';
	}

	/**
	 * Filtering based on user search text
	 * @param value - user search text
	 */
	private filter(value): any {
		this.selectedEmployer = value;
		if (typeof value === 'string') {
			this.getEmployers(value);
			const filterValue = value.toLowerCase();
			return this.employers.filter(option => option.name.toLowerCase().indexOf(filterValue) === 0);
		}
	}
	/** Create account  */
	createAccount() {
		this.errorDetails = this.dataCarrierService.getChannel().getValue().createAccountErrorDetails;
		if (this.errorDetails !== null && this.errorDetails !== undefined) {
			this.analyticsData.errorDetails = {
				errorMsg: this.errorDetails.errorInfo,
				attempt: this.errorDetails.attempt,
			};
		}
		this.analyticsData.id = 'create account';
		this.analyticsData.redirectedTo = 'verification/code';
		this.analyticsData.placement = 'Registration create account';
		// getting form details
		const accountDetails = this.createAccountForm.getRawValue();
		accountDetails.employer = accountDetails.employer.name;
		accountDetails.email = AppUtil.getFormattedEmail(accountDetails.email);
		let employer;
		this.clearValue = false;
		if (this.selectedEmployer !== undefined && this.selectedEmployer.id === undefined) {
			if (this.employerName) {
				employer = this.getEmployerByName(this.employerName);
				accountDetails.employer = employer;
			}
			if (!employer) {
				this.createAccountForm.get('employer').setErrors({ invalidEmployer: true });
			}
		}
		if (
			this.errorDetails !== null &&
			this.errorDetails !== undefined &&
			this.selectedEmployer &&
			this.selectedEmployer.id !== undefined
		) {
			this.analyticsData.errorDetails['clientID'] = this.selectedEmployer.id;
		}
		if (this.createAccountForm.valid && (this.selectedEmployer.id !== undefined || employer)) {
			if (accountDetails.ssn && accountDetails.ssn.length === 9) {
				accountDetails.ssn = accountDetails.ssn.replace(/^(\d{3})(\d{2})(\d{4})$/g, '$1-$2-$3');
			}
			if (accountDetails.employer !== this.oldEmployerName) {
				this.verificationAttemptCount = 0;
			}
			accountDetails['additionalData'] = this.getAdditionalData();
			this.createAccountService(accountDetails);
		} else if (!this.createAccountForm.get('dateOfBirth').value) {
			this.errorDetails = {
				errorInfo: 'Inline Errors',
				attempt: this.verificationAttemptCount,
			};
			this.dataCarrierService.update('createAccountErrorDetails', this.errorDetails);
			this.invalidDateOfBirth = true;
			this.showHint = this.createAccountForm.get('dateOfBirth').errors ? false : true;
		} else {
			this.errorDetails = {
				errorInfo: 'Inline Errors',
				attempt: this.verificationAttemptCount,
			};
			this.dataCarrierService.update('createAccountErrorDetails', this.errorDetails);
		}
	}

	changeHint() {
		this.showHint = this.createAccountForm.get('dateOfBirth').errors ? false : true;
	}

	/**
	 * Sending the request to backend
	 * @param accountDetails - User form  data
	 */
	createAccountService(accountDetails: any) {
		this.oldEmployerName = accountDetails.employer;
		if (this.ssnRequired) {
			if (this.verificationAttemptCount) {
				accountDetails.allowNextAttempt = false;
			} else {
				accountDetails.allowNextAttempt = true;
			}
		} else if (this.verificationAttemptCount) {
			if (this.verificationAttemptCount < 4) {
				accountDetails.allowNextAttempt = true;
			} else {
				accountDetails.allowNextAttempt = false;
			}
		}
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.registrationApi.createAccount,
					METHOD_TYPE.GENERAL_POST,
					accountDetails
				)
				.subscribe(
					(response: any) => {
						this.hvaEvent.type = HVA_EVENT_TYPE.CREATE_ACCOUNT;
						this.hvaEvent.status = HVA_EVENT_STATUS.SUCCESS;
						this.hvaEvent.userID = response.userId;
						this.analyticsService.sendHvaEvent(this.hvaEvent);
						this.spinner.stopLoader();
						this.dataCarrierService.update('createAccountErrorDetails', null);
						this.accountCreated = true;
						// this.successMessage = `Please check <b>${emailMask(accountDetails.email, '*')}</b> for an EHE Health reset password email`;
					},
					err => {
						this.hvaEvent.type = HVA_EVENT_TYPE.CREATE_ACCOUNT;
						this.hvaEvent.status = HVA_EVENT_STATUS.FAIl;
						this.analyticsService.sendHvaEvent(this.hvaEvent);
						console.error(`[${CreateAccountComponent.name}][${this.createAccountService.name}]`, 'Error occurred while creating account ', err);
						this.handleCreateAccountErrors(err, accountDetails.email);
						this.spinner.stopLoader();
					}
				)
		);
	}

	/*
	 *  This will be called if the user exists and is in an 'Initiated' or 'Failure' migration status
	 */
	openPopupForMigratedUser(migrateStatus: string, navigationPath: string, navigationExtras: NavigationExtras) {
		const dialogRef = this.dialog.open(MigrationNotificationModalComponent, {
			width: '400px',
			disableClose: true,
			data: {
				button1: 'OK',
				modalWidth: '450px',
				title: 'Account Already Exists',
				content: `We have determined you have an existing account.  You will need to set a new password to complete the process.`,
				fromCreateAccount: true,
				migrationStatus: migrateStatus,
			},
		});

		dialogRef.afterClosed().subscribe(result => {
			this.router.navigate([navigationPath], navigationExtras);
		});
	}

	/**
	 * Handle create account errors
	 * @param err
	 */
	handleCreateAccountErrors(err, email) {
		// Migration initiated/failed for user
		if (err && err.error && err.error.data) {
			if (err.error.data.migrationStatus === 'Initiated') {
				const user = {
					email: err.error.data.registeredEmail,
					epmsPID: err.error.data.epmsPID,
					uuid: err.error.data.uuid,
				};
				// Initialized - Email exists on file - collect code and complete migration process
				const navigationExtras: NavigationExtras = {
					queryParams: {
						user: btoa(JSON.stringify(user)),
						fromSignup: true,
						returnUrl: this.returnUrl,
					},
				};

				this.openPopupForMigratedUser(err.error.data.migrationStatus, '/migration/password', navigationExtras);
			} else if (err.error.data.migrationStatus === 'Failed') {
				const user = {
					email: email,
					epmsPID: err.error.data.epmsPID,
					uuid: err.error.data.uuid,
				};
				// Failed - No email on file - get email and complete migration process
				const navigationExtras: NavigationExtras = {
					queryParams: {
						user: btoa(JSON.stringify(user)),
						fromSignup: true,
						returnUrl: this.returnUrl,
					},
				};

				this.openPopupForMigratedUser(err.error.data.migrationStatus, '/migration/email', navigationExtras);
			}
		}
		if (err && err.error && err.error.message) {
			// If user is already active in ehe&me redirecting to ehe&me site
			this.errorDetails = {
				errorInfo: err.error.message,
				attempt: this.verificationAttemptCount,
			};
			this.dataCarrierService.pushValue('createAccountErrorDetails', this.errorDetails);
			if (err.error.message === this.config.exceptions.service.USER_IS_ALREADY_MIGRATED) {
				this.openInfoModalRedirectingToLogin('User has already been migrated. Please login to continue.');
				this.analyticsData.id = 'User has been migrated';
				this.analyticsData.redirectedTo = '/login';
				this.analyticsData.placement = 'User has been migrated modal';
				this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
			} else if (
				err.error.message === this.config.exceptions.service.USER_ALREADY_ACTIVATED_ON_EHE_ME ||
				err.error.message === this.config.exceptions.service.EMPLOYER_NOT_ACTIVATED_IN_MYEHE
			) {
				const data = { title: '', content: '' };

				if (err.error.message === this.config.exceptions.service.EMPLOYER_NOT_ACTIVATED_IN_MYEHE) {
					data.title = '';
					data.content = 'Please contact Member Services at 800.362.8671 or MemberServices@ehe.health.';
					this.analyticsData.id = data.title;
					this.analyticsData.redirectedTo = this.loginUrlBase;
				} else {
					this.redirectToLogin(data);
				}

				this.dialog.open(EheMeRedirectModalComponent, {
					width: '400px',
					data: data,
					disableClose: true,
				});
				setTimeout(() => {
					let redirectURL = this.loginUrlBase;
					if (this.returnUrl) {
						redirectURL += `?returnUrl=${this.returnUrl}`;
					}
					AppUtil.navigateToUrl(AppUtil.addParamsToURL(redirectURL), this.router);
				}, 3000);
				this.analyticsData.placement = 'Registration EHE&Me user error modal';
				this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
			} else if (
				err.error.message === this.config.exceptions.service.EMAIL_TAKEN_UP ||
				err.error.message === this.config.exceptions.service.EMAIL_ALREADY_EXISTS ||
				err.error.message === this.config.exceptions.service.USER_ALREADY_AVAILABLE
			) {
				this.openInfoModalRedirectingToLogin(
					'This email is already associated with an active myEHE account. Please register with a different email or <a class="link pb-0" href="/forgot/password"><u>reset your password</u></a>. You can also contact Member Services at 800.362.8671 or <a class="link" href= "mailto:memberservices@ehe.health "> MemberServices@ehe.health</a>. <br> Reference Number: 5'
				);
				this.analyticsData.id = 'Email already exists';
				this.analyticsData.redirectedTo = '/login';
				this.analyticsData.placement = 'Registration email already exists modal';
				this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
			} else if (err.error.message === this.config.exceptions.service.EMAIL_DOES_NOT_MATCH) {
				this.openEmailDoesNotMatchModal(err.error.data);
				this.analyticsData.id = "Email doesn't match with system email";
				this.analyticsData.redirectedTo = '/registration';
				this.analyticsData.placement = "Registration email doesn't match modal";
				this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
			} else if (err.error.message === this.config.exceptions.service.EMAIL_CLAIMED_BY_PRIMARY) {
				/**
				 * Clearing email and setting duplicate email error, if spouse/ dependent tries to login with account holders email
				 */
				this.createAccountForm.get('email').setValue('');
				this.createAccountForm.get('email').setErrors({ duplicateEmail: true });
				this.analyticsData.id = 'Email claimed by primary user';
				this.analyticsData.redirectedTo = '/registration';
				this.analyticsData.placement = 'Registration email claimed by primary user modal';
				this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
			} else if (err.error.message === this.config.exceptions.service.NO_MATCH) {
				if (!this.verificationAttemptCount) {
					this.verificationField = err.error.data.additionalAttribute;
					this.createAccountForm.get('firstName').setValue('');
					this.createAccountForm.get('lastName').setValue('');
					this.createAccountForm.get('employer').setValue('');
					this.createAccountForm.get('dateOfBirth').setValue('');
					if (this.ssnRequired) {
						this.createAccountForm.get('ssn').setValue(undefined);
						this.clearValue = true;
					}
				} else if (this.verificationAttemptCount === 1) {
					this.verificationField = err.error.data.additionalAttribute;
					if (this.verificationField === 'Employee ID') {
						this.employeeIdRequired = true;
						this.createAccountForm.get('employeeID').enable();
					} else {
						this.createAccountForm.get('ssn').enable();
					}
				} else {
					if (this.verificationField === 'Employee ID') {
						this.createAccountForm.get('employeeID').setValue(undefined);
					} else {
						this.createAccountForm.get('ssn').setValue(undefined);
						this.clearValue = true;
					}
				}
				// Scrolling to top
				window.scroll(0, 0);
				this.verificationAttemptCount++;
			} else {
				console.error(`[${CreateAccountComponent.name}][${this.handleCreateAccountErrors.name}][1]`, 'An error occurred while creating account for email', this.createAccountForm.get('email'), err);
			}
		} else {
			console.error(`[${CreateAccountComponent.name}][${this.handleCreateAccountErrors.name}][2]`, 'An error occurred while creating account for email', this.createAccountForm.get('email'), err);
		}
	}

	/**
	 * Open the modal if the user mail already exist
	 */
	openInfoModalRedirectingToLogin(content) {
		const dialogRef = this.dialog.open(EheVerificationModalComponent, {
			width: '400px',
			disableClose: true,
			data: {
				button1: 'Back to login',
				modalWidth: '450px',
				content: content,
				alignCenterButtonCenter: true,
				close: true,
			},
		});
		dialogRef.afterClosed().subscribe(result => {
			this.router.navigate(['/login']);
		});
	}

	/**
	 * Trigger the modal if the user mail doesn't match with backend data
	 * @param data
	 */
	openEmailDoesNotMatchModal(data) {
		const dialogRef = this.dialog.open(EheVerificationModalComponent, {
			width: '400px',
			disableClose: true,
			data: {
				button1: 'YES',
				button2: 'NO',
				modalWidth: '450px',
				content: `The email you entered does not match the email we have on file for you: ${data.systemEmail}.
         To continue activation, would you like us to send a verification code to that email?`,
				alignCenterButtonCenter: false,
				fromCreateAccount: true,
			},
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				if (result.value === 'submit') {
					if (data.systemEmail) {
						data.systemEmail = AppUtil.getFormattedEmail(data.systemEmail);
						this.sendVerificationCodeToSystemEmail(data);
					}
				} else {
					this.openSecondPopUpForEmailDoesNotMatch();
				}
			}
		});
	}

	/**
	 * Open second PopUp if Email does'nt match
	 */
	openSecondPopUpForEmailDoesNotMatch() {
		const dialogRef = this.dialog.open(EheVerificationModalComponent, {
			width: '400px',
			disableClose: true,
			data: {
				button1: 'OK',
				modalWidth: '450px',
				content: `To continue your activation without using the email we have on file for you,
         please contact Member Services at <b>MemberServices@ehe.health</b> or by calling 800.362.8671.`,
				alignCenterButtonCenter: true,
				fromCreateAccount: true,
			},
		});
	}

	/**
	 * Sends verification code to system email
	 * @param data
	 */
	sendVerificationCodeToSystemEmail(data) {
		const request = new VerificationBean();
		request.uuid = data.uuid;
		request.emailAddress = data.systemEmail;
		request.additionalData = this.getAdditionalData();
		request.registration = true;
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.registrationApi.createAccount + `/${data.epmsPid}/verification/resend`,
					METHOD_TYPE.GENERAL_POST,
					request
				)
				.subscribe(
					(response: any) => {
						this.spinner.stopLoader();
						// Setting system email as email in create account form
						this.createAccountForm.get('email').setValue(data.systemEmail);
						this.accountCreated = true;
					},
					err => {
						console.error(`[${CreateAccountComponent.name}][${this.sendVerificationCodeToSystemEmail.name}]`, 'An error occurred while sending verification code for email', data.systemEmail, err);
						this.spinner.stopLoader();
					}
				)
		);
	}

	/** Gets employer by name */
	getEmployerByName(name: string) {
		let employerName: string;
		if (name) {
			this.employers.forEach(employer => {
				if (name.toLowerCase().trim() === employer.name.toLowerCase()) {
					employerName = employer.name;
				}
			});
		}
		return employerName;
	}

	/**
	 * Retrieves system email from Encoded ID
	 * @param encID
	 */
	getSystemEmailFromEncID(encID) {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.registrationApi.getSystemEmail + '?encodedId=' + encID,
					METHOD_TYPE.GENERAL_GET,
					''
				)
				.subscribe(
					response => {
						this.spinner.stopLoader();
						if (response) {
							this.createAccountForm.get('email').setValue(response.email);
						} else {
							this.dialog.open(EncIdModalComponent, {
								width: '500px',
								disableClose: false,
							});
							this.systemEmailFlow = false;
							this.createAccountForm.get('email').enable();
						}
					},
					err => {
						this.dialog.open(EncIdModalComponent, {
							width: '500px',
							disableClose: false,
						});
						this.spinner.stopLoader();
						this.createAccountForm.get('email').enable();
						this.systemEmailFlow = false;
					}
				)
		);
	}

	redirect() {
		let navigationExtras: NavigationExtras;
		if (this.returnUrl !== '') {
			navigationExtras = {
				queryParams: {
					t: btoa('1'),
					returnUrl: this.returnUrl,
				},
			};
			this.router.navigate([this.loginUrl], navigationExtras);
		} else {
			navigationExtras = {
				queryParams: {
					t: btoa('1'),
				},
			};
			this.router.navigate([this.loginUrl], navigationExtras);
		}
	}

	/**
	 * Navigate to the specified url
	 * @param url
	 */
	navToLink(url) {
		window.open(url, '_blank');
	}

	private redirectToLogin(data) {
		data.title = 'Existing Account Found';
		data.content = 'You will be redirected to login with your existing account.';
		this.analyticsData.id = data.title;
		this.analyticsData.redirectedTo = this.loginUrlBase;
	}

	/** unsubscribing the subscriptions used in application */
	ngOnDestroy() {
		// prevent memory leak when component destroyed
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}

	/**
	 * Returns additional data
	 */
	getAdditionalData() {
		const additionalData = {};
		if (localStorage.getItem('utm')) {
			const params = JSON.parse(localStorage.getItem('utm'));
			additionalData['utm'] = params;
		}
		if (this.returnUrl) {
			additionalData['returnUrl'] = this.returnUrl;
		}
		if (this.router.routerState.snapshot.url.includes('embed')) {
			additionalData['fromMarketo'] = true;
		}
		// need to send promo/referral for email if it is present in url
		this.addPromoReferralDetailsIfPresent(additionalData);
		return additionalData;
	}

	addPromoReferralDetailsIfPresent(additionalData: any) {
		additionalData[INCENTIVE_CONSTANTS.RETURN_URL_ADDITIONAL_DATA_KEY] = {};
		// capture promo/referral codes either from return url or from query params
		const returnUrlParams = new HttpParams({
			fromString: this.returnUrl.includes('?') ? this.returnUrl.substring(this.returnUrl.indexOf('?') + 1) : '',
		});

		if (returnUrlParams.has(INCENTIVE_CONSTANTS.PROMO_QUERY_KEY)) {
			additionalData[INCENTIVE_CONSTANTS.RETURN_URL_ADDITIONAL_DATA_KEY][INCENTIVE_CONSTANTS.PROMO_QUERY_KEY] =
				returnUrlParams.get(INCENTIVE_CONSTANTS.PROMO_QUERY_KEY);
		}
		if (returnUrlParams.has(INCENTIVE_CONSTANTS.REFERRAL_QUERY_KEY)) {
			additionalData[INCENTIVE_CONSTANTS.RETURN_URL_ADDITIONAL_DATA_KEY][INCENTIVE_CONSTANTS.REFERRAL_QUERY_KEY] =
				returnUrlParams.get(INCENTIVE_CONSTANTS.REFERRAL_QUERY_KEY);
		}
		if (this.activatedRoute.snapshot.queryParams[INCENTIVE_CONSTANTS.PROMO_QUERY_KEY]) {
			additionalData[INCENTIVE_CONSTANTS.RETURN_URL_ADDITIONAL_DATA_KEY][INCENTIVE_CONSTANTS.PROMO_QUERY_KEY] =
				this.activatedRoute.snapshot.queryParams[INCENTIVE_CONSTANTS.PROMO_QUERY_KEY];
		}
		if (this.activatedRoute.snapshot.queryParams[INCENTIVE_CONSTANTS.REFERRAL_QUERY_KEY]) {
			additionalData[INCENTIVE_CONSTANTS.RETURN_URL_ADDITIONAL_DATA_KEY][INCENTIVE_CONSTANTS.REFERRAL_QUERY_KEY] =
				this.activatedRoute.snapshot.queryParams[INCENTIVE_CONSTANTS.REFERRAL_QUERY_KEY];
		}
	}
}
